import { useId } from "react";

import { cn } from "../lib/cn";

export interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  /** Visible label. Prefer this over `aria-label`. */
  label?: string;
  /** Quiet helper line under the field. */
  hint?: string;
  /** Error message. Its presence puts the field in the invalid state. */
  error?: string;
  ref?: React.Ref<HTMLInputElement>;
}

/**
 * Single-line text field on card stock, with label, hint and error lines wired
 * to the input via `aria-describedby` / `aria-invalid`.
 */
export function Input({
  label,
  hint,
  error,
  id,
  className,
  "aria-describedby": ariaDescribedBy,
  ...rest
}: InputProps) {
  const generatedId = useId();
  const inputId = id ?? generatedId;
  const hintId = `${inputId}-hint`;
  const errorId = `${inputId}-error`;

  const describedBy =
    [ariaDescribedBy, hint ? hintId : null, error ? errorId : null]
      .filter(Boolean)
      .join(" ") || undefined;

  return (
    <div className="flex w-full flex-col gap-1.5">
      {label && (
        <label
          htmlFor={inputId}
          className="font-body text-sm font-medium text-ink"
        >
          {label}
        </label>
      )}
      <input
        id={inputId}
        aria-invalid={error ? true : undefined}
        aria-describedby={describedBy}
        className={cn(
          "w-full rounded-paper border bg-card px-3 py-2",
          "font-body text-sm text-ink placeholder:text-faint",
          "transition duration-150 ease-paper",
          // Inputs hold their focus ink on the paper edge itself: no detached
          // outline gap between the selected field and its indigo state.
          "focus-visible:border-accent focus-visible:outline-none",
          "disabled:cursor-not-allowed disabled:bg-recess disabled:text-faint",
          error ? "border-ink-stopped" : "border-rule",
          className,
        )}
        {...rest}
      />
      {hint && (
        <p id={hintId} className="font-body text-sm text-annotation">
          {hint}
        </p>
      )}
      {error && (
        <p id={errorId} className="font-body text-sm text-ink-stopped">
          {error}
        </p>
      )}
    </div>
  );
}
